import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { Text } from 'src/modules/shared/components/atoms/text';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import {
  type CatalogProduct,
  type CustomFieldDefinition,
  CustomFieldEntityType,
  CustomFieldType,
} from 'src/api/generated';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createCustomFieldFromDefinition } from 'src/modules/shared/utils/custom-fields-factory.util';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';
import { getCustomFieldKey } from 'src/modules/shared/utils/custom-fields.util';
import { useCustomFieldListOptions } from 'src/modules/shared/hooks/api/custom-fields/list-options';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { CustomFieldFormField } from 'src/modules/shared/components/molecules/custom-fields/custom-field-form-field';
import {
  productFormSchema,
  type ProductFormInputValues,
  type ProductFormValues,
} from '../../utils/product-validations';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    values: ProductFormValues,
    customFields: Record<string, unknown>
  ) => Promise<void>;
  loading: boolean;
  product?: CatalogProduct | null;
  customFieldDefinitions?: CustomFieldDefinition[];
}

const currencyOptions = [
  { content: 'CLP', value: 'CLP' },
  { content: 'UF', value: 'UF' },
];

export const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  product,
  customFieldDefinitions = [],
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormInputValues, unknown, ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: '',
      description: '',
      price: '',
      currency: 'CLP',
      sourceUrl: '',
    },
  });

  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const { t } = useI18n();

  useEffect(() => {
    const formValues: ProductFormValues = {
      name: product?.name ?? '',
      description: product?.description ?? '',
      price: product?.price ?? '',
      currency: product?.currency ?? 'CLP',
      sourceUrl: product?.sourceUrl ?? '',
    };
    reset(formValues);

    setCustomFields(product?.customFields ?? {});
  }, [product, isOpen, reset]);

  const listDefinitionIds = useMemo(
    () =>
      customFieldDefinitions
        .filter((def) => def.fieldType === CustomFieldType.LIST)
        .map((def) => def.id),
    [customFieldDefinitions]
  );

  const optionsQuery = useMemo(() => {
    if (listDefinitionIds.length === 0) return null;

    return createQuery()
      .equals('definitionId', listDefinitionIds)
      .take(100)
      .build();
  }, [listDefinitionIds]);

  const { options: listOptions } = useCustomFieldListOptions({
    ...(optionsQuery ?? {}),
    enabled: listDefinitionIds.length > 0,
  });

  const optionsMap = useMemo(() => {
    const map: Record<number, { label: string; value: string }[]> = {};

    listOptions.forEach((option) => {
      const definitionOptions = (map[option.definitionId] ??= []);

      definitionOptions.push({
        label: option.label ?? option.value,
        value: option.value,
      });
    });

    return map;
  }, [listOptions]);

  const handleFormSubmit = async (values: ProductFormValues) => {
    await onSubmit(
      {
        name: values.name.trim(),
        description: values.description?.trim() ?? undefined,
        price: values.price?.trim() ? values.price.trim() : undefined,
        currency: values.currency,
        sourceUrl: values.sourceUrl?.trim()
          ? values.sourceUrl.trim()
          : undefined,
      },
      customFields
    );
  };

  const headerTitle = product
    ? t('catalog.products.form.edit')
    : t('catalog.products.form.create');

  const getErrorMessage = (message: unknown): string | undefined =>
    typeof message === 'string' ? message : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      dialogClassName="sm:max-w-2xl"
      header={
        <Text size="h5" weight="semibold">
          {headerTitle}
        </Text>
      }
      body={
        <form
          className="flex flex-col gap-4 w-full"
          onSubmit={handleSubmit(handleFormSubmit)}
          id="product-form"
        >
          <div className="flex flex-col gap-1">
            <Label size="md" required>
              {t('catalog.products.form.name')}
            </Label>
            <TextInput
              {...register('name')}
              placeholder="Nombre del producto"
              error={errors.name?.message}
              required
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label size="md">{t('catalog.products.form.description')}</Label>
            <TextArea
              {...register('description')}
              placeholder="Describe el producto"
              rows={4}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label size="md">{t('catalog.products.form.price')}</Label>
              <TextInput
                {...register('price')}
                placeholder="0"
                error={getErrorMessage(errors.price?.message)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label size="md">{t('catalog.products.form.currency')}</Label>
              <Controller
                control={control}
                name="currency"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Select
                      options={currencyOptions}
                      value={value}
                      onChange={(event) => onChange(event.target.value)}
                    />
                  );
                }}
              />
              {errors.currency?.message && (
                <Text size="tiny" color="danger">
                  {getErrorMessage(errors.currency.message)}
                </Text>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label size="md">{t('catalog.products.form.sourceUrl')}</Label>
            <TextInput
              {...register('sourceUrl')}
              placeholder={t('catalog.products.form.sourceUrlPlaceholder')}
              error={getErrorMessage(errors.sourceUrl?.message)}
            />
          </div>

          {customFieldDefinitions.length > 0 && (
            <>
              <Text
                size="tiny"
                weight="medium"
                className="text-secondary-500 flex items-center gap-1.5 pt-6 -mb-4 self-end"
              >
                {t('catalog.products.form.customFields')}
              </Text>

              <div className="flex flex-col gap-2">
                {customFieldDefinitions.map((definition) => {
                  const prefixedKey = getCustomFieldKey(
                    definition.fieldType,
                    definition.fieldKey
                  );
                  const value = customFields[prefixedKey] ?? null;

                  const customField = createCustomFieldFromDefinition(
                    definition.id,
                    definition.fieldKey,
                    definition.fieldType,
                    definition.fieldLabel,
                    value,
                    definition.fieldType === CustomFieldType.LIST
                      ? optionsMap[definition.id]
                      : undefined,
                    resolveDateCustomFieldIncludeTime(
                      definition.fieldType,
                      definition.includeTime
                    )
                  );

                  return (
                    <CustomFieldFormField
                      key={definition.id}
                      field={customField}
                      onChange={(nextValue) => {
                        setCustomFields((prev) => ({
                          ...prev,
                          [prefixedKey]: nextValue,
                        }));
                      }}
                      disabled={loading || isSubmitting}
                      entityType={CustomFieldEntityType.PRODUCT}
                      entityId={product?.id}
                    />
                  );
                })}
              </div>
            </>
          )}
        </form>
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSubmit(handleFormSubmit)}
          confirmText={
            product
              ? t('catalog.products.form.saveChanges')
              : t('catalog.products.form.createButton')
          }
          cancelText={t('catalog.products.form.cancel')}
          loading={loading || isSubmitting}
          confirmDisabled={loading || isSubmitting}
        />
      }
    />
  );
};
